import type { QuizScript, QuizData } from '../types'
import titleStep1Svg from '../assets/print/title-step1.svg'
import titleStep2Svg from '../assets/print/title-step2.svg'
import titleStep3Svg from '../assets/print/title-step3.svg'
import titleStep4Svg from '../assets/print/title-step4.svg'
import titleStep4FullSvg from '../assets/print/title-step4-full.svg'
import tableYSvg from '../assets/print/table-y.svg'
import tableCSvg from '../assets/print/table-c.svg'
import tableRSvg from '../assets/print/table-r.svg'

// ─── Types ──────────────────────────────────────────────────────────

export type PaperSize = 'A4' | 'Letter'
export type Orientation = 'portrait' | 'landscape'

export interface PageInfo {
  chapterNumber: string
  grade: string
  unitLabel: string
  unitName: string
  quetureTitle: string
  bookLabel: string
}

export interface PrintSettings {
  paperSize: PaperSize
  orientation: Orientation
  columns: 1 | 2
  showPageNumber: boolean
  gutterEnabled: boolean
  gutterSize: number // in mm, default 15
  bleedEnabled: boolean
  compact: boolean
  pageInfo: PageInfo
  step4Questions: string[]
}

export interface StepConfig {
  svg: string
  barColor: string
  barHeight: number
  curveHeight: number
  svgWidth: number
  svgHeight: number
}

export interface StepPage {
  stepIdx: number
  isFirstOfStep: boolean
  quizzes: QuizScript[]
  quizStartNum: number
}

// ─── Layout Constants ───────────────────────────────────────────────

export const LAYOUT = {
  // Page
  questionSpacing: 56,
  pageSidePadding: 24,

  // Quiz container top padding (varies by page context)
  quizTopPadding: {
    afterStepTitle: 40,      // Case 1: step SVG → first quiz
    step23FirstPage: 32,     // Step2/3 first page (absolute SVG + 8px offset = 40 visual)
    continuationGap: 64,     // Case 3: total visual gap from colored bar for continuation pages
  },

  // Header
  header: {
    barHeight: 55,
    curveHeight: 80,
    totalHeight: 135, // bar + curve
  },

  // Footer
  footer: {
    sideMargin: 72,
    bottomGap: 24,
  },

  // Chapter number (lesson-number.svg full size; crop by bleedPx when no bleed)
  chapterNumber: {
    width: 195,
    height: 244,
    fontSize: 64,
    numberTop: 121,
    numberLeft: 74,
  },

  // Title
  title: {
    top: 79,
    quetureMarginLeft: 207,
    gradeBadge: { height: 28, width: 56, fontSize: 18 },
  },

  // Step headers
  step1: { barHeight: 55, curveHeight: 80 },
  step2: { barHeight: 80, curveHeight: 40 },
  step3: { barHeight: 80, curveHeight: 40 },

  // Content area offsets
  step1FirstPaddingTop: 152,

  // Step title SVG positioning
  stepSvgLeft: 72,
  stepSvgTop: 46,
  step1SvgPadding: 72,
} as const

// ─── Defaults ───────────────────────────────────────────────────────

export const DEFAULT_PAGE_INFO: PageInfo = {
  chapterNumber: '07',
  grade: '4-1',
  unitLabel: '1단원.',
  unitName: '큰 수',
  quetureTitle: '나누기는 압축',
  bookLabel: 'PRIME-A',
}

export const DEFAULT_SETTINGS: PrintSettings = {
  paperSize: 'A4',
  orientation: 'portrait',
  columns: 1,
  showPageNumber: true,
  gutterEnabled: false,
  gutterSize: 15,
  bleedEnabled: false,
  compact: true,
  pageInfo: { ...DEFAULT_PAGE_INFO },
  step4Questions: ['1이라는 숫자가 3차원(부피), 2차원(면), 1차원(선)에서 모두 똑같이 나타날 수 있는 이유가 무엇일까?'],
}

export const DEFAULT_PRESET_IDS: string[] = [
  '13672703739302028',
  '1915840074686556',
  '16795729986487345',
  '1246751270115445',
  '17221556606342134',
  '12714521919492304',
  '4230482230747628',
  '14280924283801880',
  '20041774865060359',
  '2930127562919058',
  '19105832578847656',
  '580583781601294',
  '4047017585789966',
  '1254039175305218',
  '1247008934598797',
]

// ─── Paper Dimensions ───────────────────────────────────────────────

export const PAPER_SIZES = {
  A4: { width: 210, height: 297 },
  Letter: { width: 215.9, height: 279.4 },
} as const

// ─── Step Configuration ─────────────────────────────────────────────

export const STEP_CONFIGS: StepConfig[] = [
  { svg: titleStep1Svg, barColor: '#FEF1CB', barHeight: 55, curveHeight: 80, svgWidth: 652, svgHeight: 51 },
  { svg: titleStep2Svg, barColor: '#BBE5FA', barHeight: 80, curveHeight: 40, svgWidth: 217, svgHeight: 51 },
  { svg: titleStep3Svg, barColor: '#DEF2DA', barHeight: 80, curveHeight: 40, svgWidth: 217, svgHeight: 51 },
  { svg: titleStep4Svg, barColor: '#FFCCCC', barHeight: 80, curveHeight: 40, svgWidth: 217, svgHeight: 51 },
]

export const STEP4_TOP_TITLE_SVG = titleStep4FullSvg

export const STEP4_LAYOUT = {
  titleTopPadding: 40,
  questionGap: 16,
  noteTopGap: 16,
  noteWidth: 602,
  noteHeight: 162,
  sectionPadding: 24,
  // title(51) + padding(40) + question(~32) + gap(16) + note(162) + padding(24)
  inlineHeight: 325,
} as const

export const QUIZZES_PER_STEP = 5

// Bleed (도련) — always 3mm when enabled
export const BLEED_SIZE_MM = 3

/** 저해상도 이미지에 시각적 마커 표시 (인쇄 시 자동 제외) */
export const LOW_RES_MARKER_ENABLED = true

// ─── TOC (목차) Constants ───────────────────────────────────────────

export const TOC_MODULE_COLORS = [
  '#FF9F19', // 1st module — Orange
  '#00BFA0', // 2nd module — Teal
  '#FF6666', // 3rd module — Red
] as const

export const TOC_LAYOUT = {
  bgColor: '#FEF1CB',
  tableWidth: 642,
  tableHeight: 955,
  tableRadius: 40,
  badgeHeight: 48,
  badgeRadius: 10,
  badgeShadowSize: 12,
  badgePaddingLeft: 16,
  badgeChapterFontSize: 16,
  badgeNumberSize: 28,
  badgeNumberFontSize: 18,
  badgeNameFontSize: 20,
  badgeChapterGap: 4,
  badgeNameGap: 16,

  headerHeight: 64,
  headerWidth: 193,
  headerToModuleGap: 48,
  moduleToModuleGap: 48,
  badgeToListGap: 24,

  page1: { pt: 64, pb: 80, pl: 56, pr: 48 },
  page2: { pt: 176, pl: 48, pr: 64 },

  lessonGapFirst: 12,
  lessonGapOther: 10,
  lessonRowGap: 10,
  lessonInnerGap: 4,
  lessonListPadX: 24,
  classRowPadLeft: 32,
  classRowGap: 8,

  numFontSize: 17,
  numColor: '#B2B2B2',
  titleFontSize: 18,
  pageFontSize: 18,
  gradeFontSize: 15,
  unitColor: '#808080',
} as const

// ─── Module Summary Constants ────────────────────────────────────────

export const MODULE_SUMMARY_TABLE_SVGS = [tableYSvg, tableCSvg, tableRSvg]

// ─── Utility Functions ──────────────────────────────────────────────

export function mmToPx(mm: number): number {
  return Math.round(mm * 96 / 25.4)
}

export function getPageLayout(pageHeightPx: number) {
  const footerTop = pageHeightPx - 72
  const quizBottom = footerTop - LAYOUT.footer.bottomGap

  return {
    footerTop,
    quizBottom,
    availableHeights: {
      // step1 first: header(135) + outerPad(152) + SVG(51) + quizPad(40)
      step1First: quizBottom - LAYOUT.header.totalHeight - LAYOUT.step1FirstPaddingTop - 51 - LAYOUT.quizTopPadding.afterStepTitle,
      // step1 cont: bar(55) + 64px visual gap (quiz starts at bar + 64, overlapping curve by 16px)
      step1Cont: quizBottom - LAYOUT.header.barHeight - LAYOUT.quizTopPadding.continuationGap,
      // step2/3 first: header(120) + quizPad(32)
      stepFirst: quizBottom - (LAYOUT.step2.barHeight + LAYOUT.step2.curveHeight) - LAYOUT.quizTopPadding.step23FirstPage,
      // step2/3 cont: bar(80) + 64px visual gap
      stepCont: quizBottom - LAYOUT.step2.barHeight - LAYOUT.quizTopPadding.continuationGap,
    },
  }
}

export function toQuizData(script: QuizScript): QuizData {
  const rawData = script as unknown as Record<string, unknown>
  const qd = (rawData.quizData as QuizData) || script

  return {
    ...qd,
    type: script.type || qd.type,
    title: script.title || qd.title,
    problem: script.problem || qd.problem,
    vars: script.vars || qd.vars,
    image: script.image || qd.image,
    imageMobile: script.imageMobile || qd.imageMobile,
  } as QuizData
}
